import { useState } from 'react';
import { getLocalizedTitle, getLocalizedDescription } from '@immopersian/common';
import { useTranslation } from 'react-i18next';
import { X, Crown, Calendar, Clock, Users, Video, Sparkles, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Webinar } from '@immopersian/common';
import {
  useWebinarAccess,
  useWebinarRegistration,
  useWebinarPrice,
  useWebinarStatus,
  getWebinarCheckoutUrl,
} from '@immopersian/common';

interface WebinarDetailModalProps {
  webinar: Webinar;
  onClose: () => void;
  onJoin: (webinarId: string) => void;
}

export function WebinarDetailModal({ webinar, onClose, onJoin }: WebinarDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: accessData, isLoading: isCheckingAccess } = useWebinarAccess(webinar.id);
  const { data: registration } = useWebinarRegistration(webinar.id);
  const { data: priceData } = useWebinarPrice(webinar.id);

  const status = useWebinarStatus(webinar);
  const isPaid = !!webinar.price_id;
  // Only check registration for paid webinars
  const isRegistered = isPaid ? !!registration : false;

  // Dates for display
  const startDate = new Date(webinar.start_date);
  const endDate = webinar.end_date ? new Date(webinar.end_date) : null;

  const canJoin = accessData?.canJoin || false;

  const handlePurchase = async () => {
    setIsProcessing(true);
    try {
      const checkoutUrl = await getWebinarCheckoutUrl(webinar.id);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Purchase failed:', error);
      setIsProcessing(false);
    }
  };

  const handleJoinWebinar = () => {
    onJoin(webinar.id);
  };

  const hasDiscount = priceData?.hasDiscount && priceData?.discount;
  const displayPrice = hasDiscount ? priceData.formattedFinalPrice : priceData?.formatted;

  // Determine button state using centralized status logic
  let buttonText = '';
  let buttonAction = () => {};
  let buttonDisabled = false;
  let buttonStyle = 'bg-brand-terracotta hover:bg-brand-terracotta/90';
  let showButton = true;

  if (isCheckingAccess || isProcessing) {
    buttonText = t('webinar.loading', 'Loading...');
    buttonDisabled = true;
    buttonStyle = 'bg-gray-400 cursor-not-allowed';
  } else if (status === 'expired') {
    buttonText = t('webinar.expired', 'Webinar Expired');
    buttonDisabled = true;
    buttonStyle = 'bg-gray-400 cursor-not-allowed';
  } else if (status === 'processing') {
    // Webinar ended but recording not yet available
    buttonText = t('webinar.waitingForRecording', 'Waiting for Recording');
    buttonDisabled = true;
    buttonStyle = 'bg-gray-500 cursor-not-allowed';
  } else if (status === 'replay') {
    // Has recording available - watch replay
    buttonText = t('webinar.watchRecording', 'Watch Recording');
    buttonAction = handleJoinWebinar;
    buttonStyle = 'bg-brand-gold hover:bg-brand-gold/90';
  } else if (canJoin || (status === 'live' && !isPaid)) {
    // Can join live webinar (either has access or is free)
    buttonText = t('webinar.joinNow', 'Join Webinar Now');
    buttonAction = handleJoinWebinar;
    buttonStyle = 'bg-green-600 hover:bg-green-700 animate-pulse';
  } else if (isRegistered && status === 'upcoming') {
    buttonText = t('webinar.registered', 'Registered - Starts Soon');
    buttonDisabled = true;
    buttonStyle = 'bg-green-600 cursor-not-allowed';
  } else if (isPaid && !isRegistered) {
    buttonText = t('webinar.purchase', `Purchase • ${displayPrice || '...'}`);
    buttonAction = handlePurchase;
  } else if (status === 'upcoming') {
    // Webinar not started yet - no button needed for free webinars
    showButton = !isPaid;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col md:flex-row max-h-[90vh]">
          {/* Left side - Details */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Badge */}
            <div className="flex items-center gap-2 mb-4">
              {isPaid ? (
                <div className="inline-flex items-center gap-1 bg-brand-gold text-brand-dark px-3 py-1 rounded-full text-sm font-medium">
                  <Crown className="w-4 h-4" />
                  {t('webinar.paid', 'Paid Webinar')}
                </div>
              ) : (
                <div className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  {t('webinar.free', 'Free Webinar')}
                </div>
              )}

              {status === 'live' && (
                <div className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full"></span>
                  {t('webinar.liveNow', 'LIVE NOW')}
                </div>
              )}
            </div>

            <h2 className="text-2xl font-bold text-brand-dark mb-3">
              {getLocalizedTitle(webinar, i18n.language)}
            </h2>

            {/* Webinar info */}
            <div className="flex flex-wrap gap-4 text-sm text-brand-dark/70 mb-4">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {startDate.toLocaleDateString(i18n.language, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {startDate.toLocaleTimeString(i18n.language, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {endDate && (
                  <>
                    {' → '}
                    {endDate.toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </>
                )}
              </div>
              {webinar.max_participants && (
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {t('webinar.maxParticipants', { count: webinar.max_participants })}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="prose prose-sm prose-gray max-w-none mb-6 text-brand-dark/80">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {getLocalizedDescription(webinar, i18n.language)}
              </ReactMarkdown>
            </div>

            {/* What you get */}
            <div className="bg-brand-beige/50 rounded-xl p-4">
              <p className="font-semibold text-brand-dark mb-3">
                {t('webinar.whatYouGet', 'What you get')}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-brand-dark/80">
                  <Video className="w-4 h-4 text-brand-terracotta mt-0.5 flex-shrink-0" />
                  {t('webinar.liveAccess', 'Live access to the webinar session')}
                </li>
                <li className="flex items-start gap-2 text-sm text-brand-dark/80">
                  <CheckCircle className="w-4 h-4 text-brand-terracotta mt-0.5 flex-shrink-0" />
                  {t('webinar.interactiveSession', 'Interactive Q&A with the host')}
                </li>
                {endDate && (
                  <li className="flex items-start gap-2 text-sm text-brand-dark/80">
                    <CheckCircle className="w-4 h-4 text-brand-terracotta mt-0.5 flex-shrink-0" />
                    {t('webinar.replayAccess', 'Replay access after the session')}
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Right side - Thumbnail and action */}
          <div className="w-full md:w-80 bg-gradient-to-br from-brand-dark to-brand-dark/90 p-6 flex flex-col">
            {/* Thumbnail */}
            <div className="relative rounded-xl overflow-hidden mb-6 h-48">
              {webinar.thumbnail_url ? (
                <img
                  src={webinar.thumbnail_url}
                  alt={getLocalizedTitle(webinar, i18n.language)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-brand-terracotta/30 to-brand-gold/20 flex items-center justify-center">
                  <Video className="w-20 h-20 text-white/40" />
                </div>
              )}
            </div>

            {/* Pricing */}
            {isPaid && priceData && (
              <div className="bg-white/10 rounded-xl p-4 mb-4">
                {hasDiscount && priceData.discount ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-brand-gold animate-pulse" />
                      <span className="text-xs text-brand-gold font-semibold">
                        {priceData.discount.percentOff}% {t('courses.off', 'OFF')}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-white">
                        {priceData.discount.formattedFinalPrice}
                      </span>
                      <span className="text-lg text-white/50 line-through">
                        {priceData.formatted}
                      </span>
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      {t('courses.youSave', 'You save')} {priceData.discount.formattedAmountOff}
                    </div>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-white">
                    {priceData.formatted}
                  </div>
                )}
              </div>
            )}

            {/* Registration status (paid webinars only) */}
            {isPaid && isRegistered && (
              <div className="bg-green-600/20 border-2 border-green-500 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                  <CheckCircle className="w-4 h-4" />
                  {t('webinar.youAreRegistered', "You're registered!")}
                </div>
              </div>
            )}

            {/* Action button */}
            {showButton && (
              <button
                onClick={buttonAction}
                disabled={buttonDisabled}
                className={`w-full text-white font-semibold py-4 px-6 rounded-lg transition-colors ${buttonStyle}`}
              >
                {buttonText}
              </button>
            )}

            {/* Additional info */}
            {status === 'upcoming' && showButton && (
              <p className="text-xs text-white/60 mt-4 text-center">
                {t('webinar.notStartedYet', 'Webinar has not started yet')}
              </p>
            )}
            {status === 'upcoming' && !showButton && (
              <p className="text-xs text-white/60 mt-4 text-center">
                {t('webinar.joinWhenLive', 'Come back when the webinar is live to join')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
